import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit } from "@angular/core"
import { HubspotPost, Article } from "types"
import { ICustomer, SourceContent } from "graphql/queries/keyword.queries"
import { HubspotService } from "src/services/hubspot.service"
import mapValues from 'lodash-es/mapValues'
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete"
import { TinymceConfig, tinymceConfig } from "src/tinymce"
import { urlValid } from "src/services/validation-service"
import debounce from 'lodash-es/debounce'
import { ArticleExtractionService } from "src/services/article-extraction.service"
import { errorToast } from "src/toaster"
import { waitUntil, stripImages } from "src/utils"

@Component({
  styles: [`
    .white-container {
      background-color: white;
      border-radius: 4px;
      box-shadow: 5px 5px 8px #888888;
      margin: 10px;
      padding: 20px;
    }
    mat-form-field{
      display: block;
    }
    .post-body-container {
      height: 300px;
      overflow: auto;
    }
    .my-content {
      border: 1px solid lightgray;
    }
    .my-content.header {
      padding: 5px 0;
      border-bottom: none;
    }
    .input-group-text {
      background-color: transparent;
      border: none;
      padding-right: 10px;
      padding-left: 20px;
    }

    .input-group {
      input {
        padding-left: 0;
        padding-right: 0;
      }
    }
  `],
  selector: 'source-content-form',
  template: `
    <div class="white-container">
      <div *ngIf="loading" class="loader">loading...</div>
      <div *ngIf="!loading">
        <h4>What content do you need video for?</h4>
        <form (ngSubmit)="onSubmit()">
          <div class="row">
            <div class="col-sm-4">
              <mat-form-field>
                <mat-select [ngModel]="sourceContent.type" (ngModelChange)="updateSourceContentType($event)" name="type" [disabled]="viewOnly">
                  <mat-option value="hubspot">Import from Hubspot</mat-option>
                  <mat-option value="copypaste">Copy & Paste</mat-option>
                  <mat-option value="url">Import via public URL</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div *ngIf="showHubspotConnect" class="row">
            <div class="col">
              <button type="button" class="btn btn-primary" (click)="connectToHubspot()">Connect to Hubspot</button>
              <button type="button" (click)="onCancel()" class="d-none d-md-block float-right btn btn-link no-decoration">
                Cancel
              </button>
            </div>
          </div>

          <div *ngIf="!showHubspotConnect" class="row">
            <div class="col">

              <div *ngIf="sourceContent.type === 'hubspot'">
                <div class="my-content header">
                  <div class="input-group">
                    <div class="input-group-prepend" (click)="clearSearch($event)">
                      <div class="input-group-text">
                        <i *ngIf="!hasSourceContent" class="fas fa-search text-secondary"></i>
                        <i *ngIf="hasSourceContent" class="fas fa-times text-danger"></i>
                      </div>
                    </div>
                    <input
                      class="form-control no-border"
                      placeholder="Search your Hubspot content"
                      [disabled]="viewOnly"
                      [matAutocomplete]="auto"
                      [ngModel]="searchFilter"
                      (change)="setSearchFilter($event.target.value)"
                      (keyup)="debouncedSearch($event.target.value)"
                      name="searchFilter"
                      #searchInput
                      #trigger="matAutocompleteTrigger">
                  </div>
                  <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onOptionSelected($event)">
                    <mat-option *ngFor="let post of customer.hubspotPosts" [value]="post">
                      {{post.title}}
                    </mat-option>
                    <mat-option *ngIf="customer.hubspotPosts && customer.hubspotPosts.length === 0" disabled>
                      No results match your search
                    </mat-option>
                  </mat-autocomplete>
                </div>
                <div class="post-body-container my-content p-3" [innerHTML]="sourceContentHtml"></div>
              </div>

              <div *ngIf="sourceContent.type === 'copypaste'">
                <editor
                  class="copypaste"
                  [disabled]="viewOnly"
                  [ngModel]="sourceContent.content"
                  (ngModelChange)="sourceContent.content = $event"
                  name="content"
                  [init]="tinymceConfig">
                </editor>
              </div>

              <div *ngIf="sourceContent.type === 'url'">
                <div class="my-content header">
                  <div class="input-group">
                    <div class="input-group-prepend" (click)="clearSearch($event)">
                      <div class="input-group-text">
                        <i *ngIf="!hasSourceContent" class="fas fa-link text-secondary"></i>
                        <i *ngIf="hasSourceContent" class="fas fa-times text-danger"></i>
                      </div>
                    </div>
                    <input
                      class="form-control no-border"
                      placeholder="Enter a valid URL"
                      [(ngModel)]="url"
                      name="url"
                      #searchInput>
                    <button *ngIf="url && url !== importedUrl"
                      type="button"
                      class="btn btn-secondary mr-1"
                      (click)="importPostFromUrl(url)"
                      [disabled]="!urlValid(url)">
                      Import content
                    </button>
                  </div>
                </div>
                <div class="post-body-container my-content p-3">
                  <div *ngIf="urlImportInProgress">loading...</div>
                  <div *ngIf="hasSourceContent" [innerHTML]="sourceContentHtml"></div>
                </div>
              </div>

            </div>
          </div>

          <div class="row top-buffer">
            <div class="col">
              <div class="d-md-flex flex-md-row justify-content-between align-items-center" *ngIf="!showHubspotConnect">
                <div>
                  <button id="activate-keyword-btn"
                    type="submit"
                    class="btn btn-secondary btn-block-sm-down"
                    [disabled]="!hasSourceContent || viewOnly">
                    Use this content
                  </button>
                  <button type="button"
                    *ngIf="sourceContent.type === 'hubspot'"
                    [disabled]="viewOnly"
                    (click)="clearSearch($event)"
                    class="btn btn-outline-secondary btn-block-sm-down ml-md-1 mt-1 mt-md-0">
                    Search for content
                  </button>
                </div>
                <div>
                  <button type="button"
                    (click)="onCancel()"
                    class="d-none d-md-block float-right btn btn-link no-decoration">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  `
})
export class SourceContentForm implements OnInit {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('trigger', { read: MatAutocompleteTrigger }) autocompleteTrigger: MatAutocompleteTrigger;
  @Input() customer: ICustomer;
  @Input() viewOnly: boolean;
  @Input() sourceContent: SourceContent = {
    type: 'hubspot',
    content: '',
    externalIdentifier: '',
    id: null
  };
  @Output() create: EventEmitter<{sourceContent: SourceContent, title: string}> = new EventEmitter();
  @Output() cancel: EventEmitter<null> = new EventEmitter();
  @Output() hideForm: EventEmitter<null> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter();

  searchFilter: string = ''
  hsPosts: ICustomer['hubspotPosts'] = [];

  loading: boolean
  urlImportInProgress: boolean = false
  url: string
  importedUrl: string

  // Imported from Hubspot or URL
  hubspotPost: HubspotPost = {
    id: null,
    title: null,
    postBody: null,
    postSummary: null
  }
  urlPost: Article = {
    title: null,
    html: null
  }

  tinymcePlaceholder = '';

  tinymceConfig: TinymceConfig = tinymceConfig(<TinymceConfig>{
    placeholder: this.tinymcePlaceholder,
    placeholder_attrs: {
      style: {
        "font-size": '16px'
      }
    },
    height: 300
  })


  // constructor
  constructor(
    private hubspotService: HubspotService,
    private articleExtractionService: ArticleExtractionService
  ) { }

  ngOnInit(): void {
    this.waitForPlaceholder()
      .then(() => {
        this.tinymcePlaceholder = 'Paste your content here...'
        const placeHolderEl = document.querySelector('editor.copypaste placeholder')
        if (placeHolderEl) placeHolderEl.innerHTML = this.tinymcePlaceholder
      })
      .catch(e => console.log('Timed out waiting for TinyMCE placeholder', e))
  }

  // ng lifecycle hooks
  ngOnChanges(): void {
    if (!this.url && this.sourceContent.type === 'url') {
      this.url = this.sourceContent.externalIdentifier
      this.importedUrl = this.url
    }
  }

  // getters
  get hasSourceContent(): boolean {
    return !!this.sourceContent.content
  }

  get showHubspotConnect(): boolean {
    return this.sourceContent.type === 'hubspot' && this.customer && !this.customer.hubspotCredential
  }

  get sourceContentHtml(): string {
    return stripImages(this.sourceContent.content)
  }

  // methods used in the template
  connectToHubspot(): void {
    this.hubspotService.connectToHubspot({customerId: this.customer.id, redirectPage: 'topics/new/99' })
  }

  updateSourceContentType(type: SourceContent['type']): void {
    this.sourceContent = {
      ...mapValues(this.sourceContent, v => null),
      type
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.hubspotPost = event.option.value
    this.sourceContent = {
      ...this.sourceContent,
      type: 'hubspot',
      content: this.hubspotPost.postBody,
      externalIdentifier: this.hubspotPost.id.toString(),
      id: null
    }
    this.searchFilter = this.hubspotPost.title
  }

  importPostFromUrl(url: string): void {
    if (this.urlValid(url)) {
      this.urlImportInProgress = true
      this.articleExtractionService.extractFromUrl({url}).then(article => {
        this.urlImportInProgress = false
        this.importedUrl = url
        this.urlPost = article
        this.sourceContent = {
          ...this.sourceContent,
          type: 'url',
          content: this.urlPost.html,
          externalIdentifier: url
        }
      }).catch(err => {
        this.urlImportInProgress = false
        errorToast(err && err.body && err.body.message ? err.body.message : 'Something went wrong, please try again')
      })
    }
  }

  clearSearch(event): void {
    event.stopPropagation()
    if (!this.viewOnly) {
      if (this.hasSourceContent) {
        this.sourceContent = {
          ...mapValues(this.sourceContent, v => null),
          type: this.sourceContent.type
        }
        this.setSearchFilter('')
        this.url = ''
        this.searchInput.nativeElement.focus()
      }
      if (this.sourceContent.type === 'hubspot')
        this.autocompleteTrigger.openPanel()
    }
  }

  onSubmit(): void {
    if (this.sourceContent.type === 'copypaste') {
      this.sourceContent.externalIdentifier = 'copypaste'
    } else if (this.sourceContent.type === 'url') {
      this.sourceContent.externalIdentifier = this.url
    }
    let title = ''
    if (this.sourceContent.type === 'hubspot')
      title = this.hubspotPost.title
    else if (this.sourceContent.type === 'url')
      title = this.urlPost.title
    this.create.emit({sourceContent: this.sourceContent, title})
  }

  onCancel(): void {
    if (this.viewOnly) this.hideForm.emit(null)
    else this.cancel.emit(null)
  }

  urlValid(url: string): boolean {
    return urlValid(url)
  }

  setSearchFilter(query: string): void {
    this.searchFilter = query
    this.search.emit(query)
  }

  waitForPlaceholder = waitUntil(() => document.querySelector('editor.copypaste placeholder'), 100, 2000);

  debouncedSearch = debounce(this.setSearchFilter, 300);

  // private methods
}
